import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import Notes from "./Notes";
import Defects from "./Defects";
import Header from "./Header";
import { getServiceWebBaseReport } from "services/services/serviceDetails/detail";
import { getServiceDefects } from "services/services/serviceDefects/defects";
import { showError } from "../../../../redux/common/actions";
import { useDispatch } from "react-redux";
import ServiceSession from "components/Modules/ServiceSessions";
import ServiceStages from "components/Modules/ServiceSessions/ServiceStages";
import { findWindows } from "windows-iana";
import ActionButtonStyle from 'styles/application/ActionButtonStyle';

const AT = ActionButtonStyle();

const useStyles = makeStyles()((theme) => ({
	reportContainer: {
		marginTop: 25,
	},
	stageheader: {
		fontWeight: 800,
		fontSize: 20,
		marginBottom: "10px",
	},
	printResponsive: {
		marginTop: 25,
		paddingLeft: 5,
	},
	goToTopButton: {
		"&.MuiButton-root": {
		backgroundColor: "white",
		border: "1px solid #D2D2D9",
		color: "#679ced",

		
	},"&:hover":{backgroundColor:'#f8f8f8!important'} },
}));

const ServiceReport = ({ state, serviceId, customCaptions, isPrint }) => {
	const redxDispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const { classes, cx } = useStyles();
	const [serviceReportWeb, setServiceReportWeb] = useState([]);
	const [defects, setDefects] = useState([]);
	const [showGoTop, setShowGoTop] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setShowGoTop(window.scrollY > 300);
		};

        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		const fetchReport = async () => {
			try {
				const [defects, webServiceReport] = await Promise.all([
					getServiceDefects(serviceId),
					getServiceWebBaseReport(
						serviceId,
						findWindows(Intl.DateTimeFormat().resolvedOptions().timeZone)
					),
				]);
				if (defects.status) {
					setDefects(defects.data);
					setServiceReportWeb(webServiceReport?.data);
				} else {
					redxDispatch(
						showError(defects?.data?.detail || "Could not show service report")
					);
				}
			} catch (error) {
				redxDispatch(
					showError(error?.data?.detail || "Could not show service report")
				);
			}
			setLoading(false);
		};
		fetchReport();
	}, [serviceId, redxDispatch]);
	function setQuestionResponse(question) {
		switch (question.type) {
			case "S":
				return question?.valueString || "";

			case "L":
				return question?.valueString || "";

			case "N":
				return question?.valueNumeric?.toString() ?? "";

			case "D":
				return question?.valueDate ? question?.valueDate : "";

			case "T":
				return question?.valueDate ? question?.valueDate : "";

			case "O":
				return (
					question?.sessionQuestionOptions || question?.taskQuestionOptions
				);

			case "C":
				return (
					question?.sessionQuestionOptions || question?.taskQuestionOptions
				);
			case "B":
				return question?.valueBoolean
					? question?.checkboxCaption || ""
					: question?.sessionQuestionOptions || "";

			default:
				return question?.valueBoolean || "";
		}
	}

	function formatQuestion(questions, name) {
		return questions?.map((q) => {
			return {
				...q,
				date: q?.date || "",
				response: setQuestionResponse(q),
				displayName: name,
			};
		});
	}

	const groupByZone = (Zonedata) => {
		let zoneGroupedData = Zonedata.reduce((group, current) => {
			const { zoneName } = current;
			if (zoneName) {
				group[zoneName] = group[zoneName] ?? [];
				group[zoneName].push(current);
			} else {
				group["empty"] = group["empty"] ?? [];
				group["empty"].push(current);
			}
			return group;
		}, {});

		return Object.keys(zoneGroupedData).map((key) => {
			let zoneObject = {};
			return {
				...zoneObject,
				zonename: key === "empty" ? null : key,
				tasks: zoneGroupedData[key],
			};
		});
	};

	const groupByStage = (data) => {
		let stageGroupedData = data.reduce((group, current) => {
			const { stageName } = current;
			if (stageName) {
				group[stageName] = group[stageName] ?? [];
				group[stageName].push(current);
			}
			return group;
		}, {});

		return Object.keys(stageGroupedData).map((key) => {
			let stageObject = {};
			return {
				...stageObject,
				stagename: key,
				stageData: groupByZone(stageGroupedData[key]),
			};
		});
	};

	if (loading) {
		return <CircularProgress />;
	}

  const handleGoToCurrentTask = () => {
    const currentTaskElement = document.getElementById('current-task');
    if (currentTaskElement) {
      currentTaskElement.scrollIntoView({behavior: 'smooth'});
    }
  }  
	const handleGoToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}
 
	return (
		<div
			className={isPrint ? classes.printResponsive : classes.reportContainer}
		>
				{serviceReportWeb?.incompleteTaskList?.length > 0 && (
					<Box sx={{ textAlign: 'right', my: 3 }}>
						<AT.GeneralButton
									className={classes.goToTopButton}
									onClick={handleGoToCurrentTask}
								>
									Go To Current Task
						</AT.GeneralButton>
					</Box>
				)}
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Header
						state={state?.serviceDetail}
						customCaptions={customCaptions}
					/>
				</Grid>
				<Grid item xs={12}>
					<Notes data={serviceReportWeb?.noteList} isPrint={isPrint} />
				</Grid>
				<Grid item xs={12}>
					<ServiceSession
						completedService={serviceReportWeb.completedServiceList}
						roleName={state?.serviceDetail?.role}
						customCaptions={customCaptions}
						formatQuestion={formatQuestion}
						groupByStage={groupByStage}
						isPrint={isPrint}
					/>
				</Grid>
				{serviceReportWeb?.incompleteTaskList?.length > 0 && (
					<Grid item xs={12}>
						<Typography id="current-task" className={classes.stageheader}>
							{`Incomplete ${customCaptions.taskPlural}`}
						</Typography>
						<ServiceStages
							tasks={groupByStage(serviceReportWeb?.incompleteTaskList)}
							customCaptions={customCaptions}
							formatQuestion={formatQuestion}
							isIncomplete={true}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Defects
						data={defects}
						columns={[
							"number",
							"taskName",
							"stageName",
							"zoneName",
							"defectTypeName",
							"riskRatingName",
							"createdUserName",
							"details",
						]}
						customCaptions={customCaptions}
						headers={[
							{ id: 1, name: "Number" },
							{
								id: 2,
								name: customCaptions?.task,
							},
							{
								id: 3,
								name: customCaptions?.stage,
							},
							{
								id: 4,
								name: customCaptions?.zone,
							},
							{
								id: 5,
								name: customCaptions?.defectType,
							},
							{
								id: 8,
								name: customCaptions?.riskRating,
							},
							{
								id: 6,
								name: customCaptions?.user,
							},
							{
								id: 7,
								name: `${customCaptions.defect} Details`,
							},
						]}
					/>
				</Grid>
			</Grid>

				{showGoTop && (
				<Box sx={{ textAlign: 'center', my: 3 }}>
					<AT.GeneralButton
								className={classes.goToTopButton}
								onClick={handleGoToTop}
							>
								Go To Top
					</AT.GeneralButton>
				</Box>
			)}
		</div>
	);
};

export default ServiceReport;
